"use client";

import {PortableText} from "@portabletext/react";
import { richTextComponent} from "@/sanity/sanityPortableText";
import Image from "next/image";
import type {PageAffiliateSchema} from "@/sanity/pages/PageAffiliateTemplate";
import {motion} from "framer-motion";
import {Fragment, useState} from "react";

import ribbonHighlight from "@/public/affiliate/ribbon-highlight.jpg";
import arrow from "@/public/affiliate/arrow.svg";

type TextFieldType = "text1" | "text2" | "text3" | "text4" | "text5";

export default function Deals({ data }: { data: PageAffiliateSchema }) {
  const [selectedTextField, setSelectedTextField] = useState<{
    [key: number]: TextFieldType;
  }>(() => {
    // Initialize with text1 selected for all items by default
    const initialState: { [key: number]: TextFieldType } = {};
    data?.dealsItems?.forEach((_, index) => {
      initialState[index] = "text1";
    });
    return initialState;
  });
  return (
    <section className="w-full lg:px-0 px-[2rem]">
      <div className="w-[min(230rem,100%)] mx-auto bg-[#103037] rounded-[1.5rem] lg:rounded-[5rem] px-[2rem] py-[3.75rem] lg:py-[16.25rem]">
        <div className="relative mx-auto w-[min(178.5rem,100%)]">
          <motion.div
            initial={{ opacity: 0, y: -40, filter: "blur(10px)" }}
            whileInView={{ opacity: 1, y: 0, filter: "blur(0px)" }}
            viewport={{ once: true, margin: "-100px" }}
            transition={{
              duration: 0.3,
              ease: [0.25, 0.4, 0.25, 1],
            }}
          >
            {/* Title */}
            {data?.dealsTitle && (
              <div className="mb-[5rem] richtext richtext-center prose-headings:text-white">
                <PortableText
                  value={data?.dealsTitle}
                  components={richTextComponent}
                />
              </div>
            )}
          </motion.div>

          {/* Items */}
          {data?.dealsItems && data.dealsItems.length > 0 && (
            <div className="flex flex-col gap-[2.5rem] lg:gap-[1.5rem]">
              {data.dealsItems.map((item, index) => (
                <motion.div
                  key={index}
                  className="flex relative min-h-[45rem] lg:p-[1rem] overflow-clip"
                  initial={{ opacity: 0, x: -50, filter: "blur(10px)" }}
                  whileInView={{ opacity: 1, x: 0, filter: "blur(0px)" }}
                  viewport={{ once: true, margin: "-100px" }}
                  transition={{
                    duration: 0.3,
                    delay: index * 0.1,
                    ease: [0.25, 0.4, 0.25, 1],
                  }}
                >
                  <div
                    className="flex relative flex-col items-center w-full border lg:flex-row py-[2rem] px-[2rem] lg:pr-[5rem] rounded-[2.75rem] border-black/5 gap-[5rem] min-h-[45rem]"
                    style={{ backgroundColor: item.backgroundColor }}
                  >
                    <div
                      className={`flex flex-col justify-between p-[2rem] lg:p-[3rem] lg:py-[4.5rem] rounded-[2rem] flex-shrink-0 w-[min(52.375rem,100%)] lg:self-stretch`}
                      style={{ backgroundColor: item.backgroundColorTitle }}
                    >
                      <div>
                        {/* Chip */}
                        {item.chip && (
                          <div className="mb-[2rem]">
                            <div className="rounded-full bg-[#103037] p-[1.125rem] w-fit">
                              <p
                                className={`text-white text-[1.75rem] leading-[100%] font-[500] m-0 uppercase`}
                              >
                                {item.chip}
                              </p>
                            </div>
                          </div>
                        )}

                        {/* Title */}
                        {item.title && (
                          <h3
                            className={`text-[#103037] text-[4.375rem] leading-[120%] font-[500] mb-[2rem]`}
                            style={{ filter: "url(#innerShadow)" }}
                          >
                            {item.title}
                          </h3>
                        )}
                      </div>

                      <div className="">
                        {/* Tags */}
                        {item.tagsTitle && (
                          <p
                            className={`text-[#103037]/60 text-[1.75rem] leading-[135%] font-[500] uppercase`}
                          >
                            {item.tagsTitle}
                          </p>
                        )}

                        {item.tags && item.tags.length > 0 && (
                          <div className="flex flex-wrap gap-[1rem]">
                            {item.tags.map((tag, tagIndex) => (
                              <div
                                key={tagIndex}
                                className={`rounded-full bg-[#10303726]  py-[0.5rem] px-[1.5rem]`}
                              >
                                <p className="font-sans text-[2rem] m-0 leading-[125%] font-[400] text-[#17345D]">
                                  {tag.text}
                                </p>
                              </div>
                            ))}
                          </div>
                        )}
                        {item?.callout && (
                          <div className="w-full mt-[3rem]">
                            <p
                              className={`text-[#103037] text-[2.25rem] m-0 font-[400] font-sf leading-[134%] tracking-[-0.0675rem]`}
                            >
                              {item?.callout}
                            </p>
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Table */}
                    <div className="flex flex-col flex-1 justify-center py-[2rem] w-full">
                      {item.table &&
                        item.table.tableItem &&
                        item.table.tableItem.length > 0 && (
                          <Fragment>
                            <table className="hidden w-full border-collapse lg:block relative">
                              {item?.showSideBanner && (
                                <div className="hidden lg:block absolute -left-[3rem] -right-[1.5rem] top-[5.5rem] h-[9.75rem] bg-[#84ACC9] rounded-[1rem] pointer-events-none">
                                  <Image
                                    className="!w-[66.25rem] !h-full object-cover mix-blend-luminosity opacity-30 absolute right-0 top-0 rounded-[1rem]"
                                    src={ribbonHighlight}
                                    alt="ribbon background"
                                  />
                                  <div className="absolute w-[66.25rem] h-full right-0 top-0 bg-gradient-to-r from-[#84ACC9] rounded-[1rem]" />
                                  <Image
                                    className="absolute bottom-0 translate-y-[2.5rem] -right-[2.5rem] !w-[5rem]"
                                    src={arrow}
                                    alt="arrow"
                                  />
                                </div>
                              )}

                              <tbody className="flex h-full justify-between relative">
                                {item.table.tableItem.map(
                                  (column, columnIndex) => {
                                    const isLast = columnIndex === item.table.tableItem.length - 1;
                                    const textSize = isLast ? "prose-p:text-[1.75rem]" : "prose-p:text-[2rem]";
                                    return (
                                      <tr
                                        key={columnIndex}
                                        className="h-full flex-1 flex"
                                      >
                                        {/* Column Header */}
                                        <td className="h-full align-top w-full">
                                          <div className="border-b border-black/10">
                                            <p
                                              className="font-sf text-[#111820]/50 text-[1.5rem] leading-[120%] uppercase"
                                              dangerouslySetInnerHTML={{
                                                __html: column.title,
                                              }}
                                            />
                                          </div>
                                          {/* Text1 */}
                                          <div className={`richtext richtext-start prose-strong:text-[#111820] prose-p:text-[#111820]/80 prose-p:font-sf ${textSize} prose-p:leading-[135%] prose-em:not-italic prose-em:line-through prose-strong:font-[500] h-[5rem] mt-[2rem] justify-center flex flex-col`}>
                                            <PortableText
                                              value={column.text1 ?? []}
                                              components={richTextComponent}
                                            />
                                          </div>
                                          <hr className="w-full border-t border-black/10 my-[2.5rem]" />
                                          {/* Text2 */}
                                          <div className={`richtext richtext-start prose-strong:text-[#111820] prose-p:text-[#111820]/80 prose-p:font-sf ${textSize} prose-p:leading-[135%] prose-em:not-italic prose-em:line-through prose-strong:font-[500] h-[5rem] justify-center flex flex-col`}>
                                            <PortableText
                                              value={column.text2 ?? []}
                                              components={richTextComponent}
                                            />
                                          </div>
                                          <hr className="w-full border-t border-black/10 my-[2.5rem]" />
                                          {/* Text3 */}
                                          <div className={`richtext richtext-start prose-strong:text-[#111820] prose-p:text-[#111820]/80 prose-p:font-sf ${textSize} prose-p:leading-[135%] prose-em:not-italic prose-em:line-through prose-strong:font-[500] h-[5rem] justify-center flex flex-col`}>
                                            <PortableText
                                              value={column.text3 ?? []}
                                              components={richTextComponent}
                                            />
                                          </div>
                                          {/* Text4 */}
                                          {column.text4 && (
                                            <Fragment>
                                              <hr className="w-full border-t border-black/10 my-[2.5rem]" />
                                              <div className={`richtext richtext-start prose-strong:text-[#111820] prose-p:text-[#111820]/80 prose-p:font-sf ${textSize} prose-p:leading-[135%] prose-em:not-italic prose-em:line-through prose-strong:font-[500] h-[5rem] justify-center flex flex-col`}>
                                                <PortableText
                                                  value={column.text4 ?? []}
                                                  components={richTextComponent}
                                                />
                                              </div>
                                            </Fragment>
                                          )}

                                          {column.text5 && (
                                            <Fragment>
                                              <hr className="w-full border-t border-black/10 my-[2.5rem]" />
                                              <div className={`richtext richtext-start prose-strong:text-[#111820] prose-p:text-[#111820]/80 prose-p:font-sf ${textSize} prose-p:leading-[135%] prose-em:not-italic prose-em:line-through prose-strong:font-[500] h-[5rem] justify-center flex flex-col`}>
                                                <PortableText
                                                  value={column.text5 ?? []}
                                                  components={richTextComponent}
                                                />
                                              </div>
                                            </Fragment>
                                          )}
                                        </td>
                                      </tr>
                                    );
                                  },
                                )}
                              </tbody>
                            </table>

                            <div className="w-full lg:hidden space-y-[4rem]">
                              {/* First row - horizontal layout with clickable text fields */}
                              {item.table.tableItem[0] && (
                                <div className="mb-[2rem]">
                                  {/* Title */}
                                  {item.table.tableItem[0].title && (
                                    <p
                                      className={`px-[1rem] font-sf text-[1.5rem] leading-[120%] uppercase [&_br]:hidden [&_br]:after:content-['_'] [&_br]:after:inline-block m-0 mb-[1rem] text-[#111820]/50`}
                                      dangerouslySetInnerHTML={{
                                        __html:
                                          item.table.tableItem[0].title.replace(
                                            /<br\s*\/?>/gi,
                                            " ",
                                          ),
                                      }}
                                    />
                                  )}
                                  {/* Text fields in a row */}
                                  <div className="w-full max-w-full overflow-x-auto rounded-full border border-[#435269]/[15%] [&::-webkit-scrollbar]:hidden [-ms-overflow-style:none] [scrollbar-width:none]">
                                    <div className="flex gap-[.25rem] p-[.5rem] w-max">
                                      {/* Text1 */}
                                      <button
                                        onClick={() =>
                                          setSelectedTextField((prev) => ({
                                            ...prev,
                                            [index]: "text1",
                                          }))
                                        }
                                        className={`py-[1rem] px-[1.25rem] w-fit rounded-full transition-all ${
                                          selectedTextField[index] === "text1"
                                            ? "bg-[#103037]"
                                            : ""
                                        }`}
                                      >
                                        <div
                                          className={`richtext richtext-start prose-p:font-sf prose-p:text-[2rem] prose-p:leading-[135%] prose-em:not-italic prose-em:line-through prose-strong:font-[500] ${
                                            selectedTextField[index] === "text1"
                                              ? "prose-p:text-[#FFFFFF]"
                                              : "prose-p:text-[#111820]"
                                          }`}
                                        >
                                          <PortableText
                                            value={
                                              item.table.tableItem[0].text1 ?? []
                                            }
                                            components={richTextComponent}
                                          />
                                        </div>
                                      </button>
                                      {/* Text2 */}
                                      <button
                                        onClick={() =>
                                          setSelectedTextField((prev) => ({
                                            ...prev,
                                            [index]: "text2",
                                          }))
                                        }
                                        className={`py-[1rem] px-[1.25rem] w-fit rounded-full transition-all ${
                                          selectedTextField[index] === "text2"
                                            ? "bg-[#103037]"
                                            : ""
                                        }`}
                                      >
                                        <div
                                          className={`richtext richtext-start prose-p:font-sf prose-p:text-[2rem] prose-p:leading-[135%] prose-em:not-italic prose-em:line-through prose-strong:font-[500] ${
                                            selectedTextField[index] === "text2"
                                              ? "prose-p:text-[#FFFFFF]"
                                              : "prose-p:text-[#111820]"
                                          }`}
                                        >
                                          <PortableText
                                            value={
                                              item.table.tableItem[0].text2 ?? []
                                            }
                                            components={richTextComponent}
                                          />
                                        </div>
                                      </button>
                                      {/* Text3 */}
                                      <button
                                        onClick={() =>
                                          setSelectedTextField((prev) => ({
                                            ...prev,
                                            [index]: "text3",
                                          }))
                                        }
                                        className={`py-[1rem] px-[1.25rem] w-fit rounded-full transition-all ${
                                          selectedTextField[index] === "text3"
                                            ? "bg-[#103037]"
                                            : ""
                                        }`}
                                      >
                                        <div
                                          className={`richtext richtext-start prose-p:font-sf prose-p:text-[2rem] prose-p:leading-[135%] prose-em:not-italic prose-em:line-through prose-strong:font-[500] ${
                                            selectedTextField[index] === "text3"
                                              ? "prose-p:text-[#FFFFFF]"
                                              : "prose-p:text-[#111820]"
                                          }`}
                                        >
                                          <PortableText
                                            value={
                                              item.table.tableItem[0].text3 ?? []
                                            }
                                            components={richTextComponent}
                                          />
                                        </div>
                                      </button>
                                      {/* Text4 */}
                                      {item.table.tableItem[0].text4 && (
                                        <button
                                          onClick={() =>
                                            setSelectedTextField((prev) => ({
                                              ...prev,
                                              [index]: "text4",
                                            }))
                                          }
                                          className={`py-[1rem] px-[1.25rem] w-fit rounded-full transition-all ${
                                            selectedTextField[index] === "text4"
                                              ? "bg-[#103037]"
                                              : ""
                                          }`}
                                        >
                                          <div
                                            className={`richtext richtext-start prose-p:font-sf prose-p:text-[2rem] prose-p:leading-[135%] prose-em:not-italic prose-em:line-through prose-strong:font-[500] ${
                                              selectedTextField[index] === "text4"
                                                ? "prose-p:text-[#FFFFFF]"
                                                : "prose-p:text-[#111820]"
                                            }`}
                                          >
                                            <PortableText
                                              value={
                                                item.table.tableItem[0].text4 ??
                                                []
                                              }
                                              components={richTextComponent}
                                            />
                                          </div>
                                        </button>
                                      )}
                                      {/* Text5 */}
                                      {item.table.tableItem[0].text5 && (
                                        <button
                                          onClick={() =>
                                            setSelectedTextField((prev) => ({
                                              ...prev,
                                              [index]: "text5",
                                            }))
                                          }
                                          className={`py-[1rem] px-[1.25rem] w-fit rounded-full transition-all ${
                                            selectedTextField[index] === "text5"
                                              ? "bg-[#103037]"
                                              : ""
                                          }`}
                                        >
                                          <div
                                            className={`richtext richtext-start prose-p:font-sf prose-p:text-[2rem] prose-p:leading-[135%] prose-em:not-italic prose-em:line-through prose-strong:font-[500] ${
                                              selectedTextField[index] === "text5"
                                                ? "prose-p:text-[#FFFFFF]"
                                                : "prose-p:text-[#111820]"
                                            }`}
                                          >
                                            <PortableText
                                              value={
                                                item.table.tableItem[0].text5 ??
                                                []
                                              }
                                              components={richTextComponent}
                                            />
                                          </div>
                                        </button>
                                      )}
                                    </div>
                                  </div>
                                </div>
                              )}

                              {/* Remaining rows - vertical layout showing only selected field */}
                              <div className="px-[2rem]">
                                {selectedTextField[index] &&
                                  item.table.tableItem
                                    .slice(1)
                                    .map((column, columnIndex) => (
                                      <div
                                        key={columnIndex}
                                        className="flex items-start border-b last:border-b-0 gap-[2rem] mb-[2rem] pb-[2rem] border-black/10"
                                      >
                                        {/* Title */}
                                        <div className="flex-1">
                                          <p
                                            className={`font-sf text-[2rem] leading-[120%] uppercase m-0 ${index === 0 ? "text-[#111820]/50" : "text-[#163E03]/50"}`}
                                            dangerouslySetInnerHTML={{
                                              __html: column.title,
                                            }}
                                          />
                                        </div>
                                        {/* Selected text field */}
                                        <div className="flex-1">
                                          <div
                                            className={`richtext richtext-start prose-p:font-sf prose-p:text-[2rem] prose-p:leading-[135%] prose-em:not-italic prose-em:line-through prose-strong:font-[500] prose-p:m-0 ${index === 0 ? "prose-strong:text-[#111820] prose-p:text-[#111820]/80" : "prose-strong:text-[#163E03] prose-p:text-[#163E03]/80"}`}
                                          >
                                            <PortableText
                                              value={
                                                column[
                                                  selectedTextField[index]
                                                ] ?? []
                                              }
                                              components={richTextComponent}
                                            />
                                          </div>
                                        </div>
                                      </div>
                                    ))}
                              </div>
                            </div>
                          </Fragment>
                        )}
                    </div>
                  </div>

                  {item?.showSideBanner && (
                    <div className="absolute top-[5.75rem] -right-[6rem] rotate-45 hidden lg:flex items-center justify-center bg-[#6FD19A] h-[4.25rem] px-[5rem] z-10 drop-shadow-[0_5.353px_18.734px_rgba(0,0,0,0.25)]">
                      <div
                        className="absolute left-[.625rem] top-full h-0 w-0"
                        style={{
                          borderLeft: ".675rem solid #427762",
                          borderRight: ".675rem solid transparent",
                          borderTop: ".675rem solid #427762",
                          borderBottom: ".675rem solid transparent",
                        }}
                      />
                      <div
                        className="absolute right-[.75rem] top-full h-0 w-0"
                        style={{
                          borderRight: ".75rem solid #427762",
                          borderLeft: ".75rem solid transparent",
                          borderTop: ".75rem solid #427762",
                          borderBottom: ".75rem solid transparent",
                        }}
                      />
                      <p className="mb-0 leading-none tracking-tight text-[#103037] flex gap-[.75rem] items-center whitespace-nowrap">
                        <span className="font-semibold text-[1.75rem]">10k just $1</span>
                        <span className="text-[1.375rem] opacity-70">Start-Angebot</span>
                      </p>
                    </div>
                  )}
                </motion.div>
              ))}
            </div>
          )}
        </div>
      </div>
    </section>
  );
}
